import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { ScopeHeader } from "../components/ScopeHeader.js";
import { useMyGroups } from "../lib/scope.js";
import { ApiRequestError, apiFetch } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";

type MenuStatus = "published" | "active" | "draft" | "inactive";
type MenuItemStatus = "active" | "draft" | "inactive";
type MenuItemKind = "folder" | "page" | "menu_ref";

type Menu = {
  id: string;
  name: string;
  status: MenuStatus;
  // ADR-0068 scope fields — included on GET /tenant/menus/:id responses.
  scope?: import("../lib/scope.js").ContentScope;
  owner_account_id?: string | null;
  owner_group_id?: string | null;
  owner_group_name?: string | null;
};

type MenuItem = {
  id: string;
  parent_id: string | null;
  kind: MenuItemKind;
  label: string;
  status: MenuItemStatus;
  page_id: string | null;
  ref_menu_id: string | null;
  icon: string | null;
  sort_order: number;
};

type TenantMenuOption = { id: string; name: string; status: MenuStatus };

type PageOption = {
  id: string;
  title: string;
  route_key: string;
};

type FlatRow = {
  id: string;
  depth: number;
  item: MenuItem;
};

const MENU_STATUS_OPTIONS: MenuStatus[] = ["published", "active", "draft", "inactive"];
const ITEM_STATUS_OPTIONS: MenuItemStatus[] = ["active", "draft", "inactive"];

function menuStatusLabel(s: MenuStatus): string {
  if (s === "published") return "Published";
  if (s === "active") return "Active";
  return s[0].toUpperCase() + s.slice(1);
}

/** Hub rejects empty labels and page rows without page_id; keep new/edited rows persistable. */
const DEFAULT_NEW_MENU_ITEM_LABEL = "New item";

function sanitizeMenuItemForPersist(item: MenuItem): MenuItem {
  if (item.kind === "menu_ref") {
    return { ...item, label: "", page_id: null };
  }
  const label = item.label.trim() || DEFAULT_NEW_MENU_ITEM_LABEL;
  return { ...item, label, ref_menu_id: null };
}

function menuItemFromApiRow(row: MenuItem & { tenant_id?: string; menu_id?: string; created_at?: string; updated_at?: string }): MenuItem {
  const kind = row.kind === "menu_ref" ? "menu_ref" : row.kind === "page" ? "page" : "folder";
  return {
    id: row.id,
    parent_id: row.parent_id,
    kind,
    label: kind === "menu_ref" ? "" : row.label,
    status: row.status as MenuItemStatus,
    page_id: row.page_id,
    ref_menu_id: row.ref_menu_id ?? null,
    icon: row.icon,
    sort_order: row.sort_order
  };
}

function IconFolder() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M2 6h6l2 2h8v8H2z" />
    </svg>
  );
}

function IconReport() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 3h9l3 3v11H4z" />
      <path d="M13 3v4h4" />
      <path d="M7 11h6M7 14h6" />
    </svg>
  );
}

function IconLinkChain() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M8 12a3 3 0 010-4l2-2a3 3 0 014 4l-1 1" />
      <path d="M12 8a3 3 0 010 4l-2 2a3 3 0 01-4-4l1-1" />
    </svg>
  );
}

function buildChildrenMap(items: MenuItem[]): Map<string | null, MenuItem[]> {
  const map = new Map<string | null, MenuItem[]>();
  for (const item of items) {
    const arr = map.get(item.parent_id) ?? [];
    arr.push(item);
    map.set(item.parent_id, arr);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
      return a.id.localeCompare(b.id);
    });
  }
  return map;
}

function normalizeSort(items: MenuItem[]): MenuItem[] {
  const children = buildChildrenMap(items);
  const updated: MenuItem[] = [];
  for (const [parentId, siblings] of children.entries()) {
    siblings.forEach((item, idx) => {
      updated.push({ ...item, parent_id: parentId, sort_order: idx });
    });
  }
  return updated;
}

function flattenRows(items: MenuItem[]): FlatRow[] {
  const children = buildChildrenMap(items);
  const rows: FlatRow[] = [];
  function walk(parentId: string | null, depth: number) {
    for (const child of children.get(parentId) ?? []) {
      rows.push({ id: child.id, depth, item: child });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);
  return rows;
}

function collectDescendantIds(items: MenuItem[], rootId: string): Set<string> {
  const children = buildChildrenMap(items);
  const ids = new Set<string>();
  const queue = (children.get(rootId) ?? []).map((item) => item.id);
  while (queue.length > 0) {
    const id = queue.shift()!;
    ids.add(id);
    queue.push(...(children.get(id) ?? []).map((item) => item.id));
  }
  return ids;
}

function MenuItemTypePicker(props: {
  item: MenuItem;
  hasChildren: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  updateItem: (id: string, patch: Partial<MenuItem>) => void;
  requestSave: () => void;
  toastPush: (t: { kind: "error"; message: string }) => void;
}) {
  const { item, hasChildren, isOpen, onToggle, onClose, updateItem, requestSave, toastPush } = props;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    const panel = panelRef.current;
    if (!isOpen || !trigger || !panel) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const margin = 8;
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const openUp = ph > spaceBelow && spaceAbove >= spaceBelow;
    let top = openUp ? rect.top - ph - gap : rect.bottom + gap;
    let left = rect.left;
    const vw = window.innerWidth;
    if (left + pw > vw - margin) left = Math.max(margin, vw - margin - pw);
    if (left < margin) left = margin;
    if (top + ph > window.innerHeight - margin) top = Math.max(margin, window.innerHeight - margin - ph);
    if (top < margin) top = margin;
    setCoords({ top, left });
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return;
    }
    reposition();
    const panel = panelRef.current;
    const ro = panel ? new ResizeObserver(() => reposition()) : null;
    if (panel) ro?.observe(panel);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen, reposition]);

  return (
    <div className="menuTypePicker">
      <button
        ref={triggerRef}
        className="menuTypeTrigger"
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Change item type"
        title={item.kind === "folder" ? "Folder" : item.kind === "page" ? "Report" : "Menu reference"}
      >
        {item.kind === "folder" ? <IconFolder /> : item.kind === "page" ? <IconReport /> : <IconLinkChain />}
      </button>
      {isOpen
        ? createPortal(
            <div
              ref={panelRef}
              className="menuTypePanel menuTypeFloatingLayer"
              style={
                coords
                  ? { top: coords.top, left: coords.left, visibility: "visible" as const }
                  : { top: 0, left: 0, visibility: "hidden" as const }
              }
              role="listbox"
              aria-label="Item type"
            >
              <button
                className="menuTypeOption"
                type="button"
                role="option"
                onClick={() => {
                  if (item.kind !== "folder") {
                    updateItem(item.id, { kind: "folder", page_id: null, ref_menu_id: null });
                    requestSave();
                  }
                  onClose();
                }}
              >
                <IconFolder />
                <span>Folder</span>
              </button>
              <button
                className="menuTypeOption"
                type="button"
                role="option"
                onClick={() => {
                  if ((item.kind === "folder" || item.kind === "menu_ref") && hasChildren) {
                    toastPush({ kind: "error", message: "Folder with children cannot become a report." });
                    return;
                  }
                  if (item.kind !== "page") {
                    updateItem(item.id, { kind: "page", ref_menu_id: null });
                    requestSave();
                  }
                  onClose();
                }}
              >
                <IconReport />
                <span>Report</span>
              </button>
              <button
                className="menuTypeOption"
                type="button"
                role="option"
                onClick={() => {
                  if ((item.kind === "folder" || item.kind === "page") && hasChildren) {
                    toastPush({ kind: "error", message: "Item with children cannot become a menu reference." });
                    return;
                  }
                  if (item.kind !== "menu_ref") {
                    updateItem(item.id, { kind: "menu_ref", page_id: null, label: "", ref_menu_id: null });
                    requestSave();
                  }
                  onClose();
                }}
              >
                <IconLinkChain />
                <span>Menu reference</span>
              </button>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function BuildMenuEditor() {
  const params = useParams<{ menuId?: string }>();
  const nav = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const menuId = params.menuId ?? "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menu, setMenu] = useState<Menu | null>(null);
  const [name, setName] = useState("");
  const [status, setStatus] = useState<MenuStatus>("draft");
  // ADR-0068 scope + caller context for <ScopeHeader>.
  const [callerAccountId, setCallerAccountId] = useState<string | null>(null);
  const [callerRole, setCallerRole] = useState<"admin" | "builder" | "viewer">("viewer");
  const { groups: callerGroups } = useMyGroups();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [tenantMenus, setTenantMenus] = useState<TenantMenuOption[]>([]);
  const [menuGroups, setMenuGroups] = useState<Array<{ id: string; name: string; status: "active" | "inactive" }>>([]);
  const [groupOptions, setGroupOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [availableGroupsSelected, setAvailableGroupsSelected] = useState<string[]>([]);
  const [assignedGroupsSelected, setAssignedGroupsSelected] = useState<string[]>([]);
  const [groupOpsLoading, setGroupOpsLoading] = useState(false);
  const [menuUsages, setMenuUsages] = useState<Array<{ menu_id: string; menu_name: string; menu_status: string }>>([]);
  const [saveState, setSaveState] = useState<"idle" | "saving">("idle");
  const [saveTrigger, setSaveTrigger] = useState(0);
  const [typeMenuItemId, setTypeMenuItemId] = useState<string | null>(null);
  const pageById = useMemo(() => new Map(pages.map((page) => [page.id, page])), [pages]);
  const menuById = useMemo(() => new Map(tenantMenus.map((m) => [m.id, m])), [tenantMenus]);
  const flatRows = useMemo(() => flattenRows(items), [items]);
  const baselineSnapshotRef = useRef<string>("");
  const dragStartXRef = useRef<number | null>(null);
  const dragCurrentXRef = useRef<number | null>(null);
  const draggingIdRef = useRef<string | null>(null);
  const dropTargetRef = useRef<{ id: string; position: "before" | "after" } | null>(null);
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const hydratedRef = useRef(false);

  function getRowEl(id: string): HTMLTableRowElement | null {
    return tbodyRef.current?.querySelector<HTMLTableRowElement>(`[data-rowid="${id}"]`) ?? null;
  }

  function clearDragDOM() {
    tbodyRef.current?.querySelectorAll<HTMLTableRowElement>("[data-rowid]").forEach((tr) => {
      tr.classList.remove("menuRowDragging", "menuRowDropBefore", "menuRowDropAfter");
      const div = tr.querySelector<HTMLElement>("[data-itemcontent]");
      if (div && div.dataset.basedepth !== undefined) {
        div.style.paddingLeft = div.dataset.basedepth;
        div.style.transform = "";
      }
    });
  }

  const activePointerIdRef = useRef<number | null>(null);

  function startPointerDrag(itemId: string, e: ReactPointerEvent<HTMLElement>) {
    e.preventDefault();
    activePointerIdRef.current = e.pointerId;
    draggingIdRef.current = itemId;
    dragStartXRef.current = e.clientX;
    dragCurrentXRef.current = e.clientX;

    clearDragDOM();
    getRowEl(itemId)?.classList.add("menuRowDragging");

    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  }

  function updateDropFromPointer(clientX: number, clientY: number) {
    if (!draggingIdRef.current) return;
    dragCurrentXRef.current = clientX;

    const el = document.elementFromPoint(clientX, clientY) as Element | null;
    const tr = el?.closest?.("tr[data-rowid]") as HTMLTableRowElement | null;
    if (!tr) {
      const prev = dropTargetRef.current;
      if (prev) getRowEl(prev.id)?.classList.remove("menuRowDropBefore", "menuRowDropAfter");
      dropTargetRef.current = null;
      return;
    }

    const targetId = tr.dataset.rowid || "";
    if (!targetId) return;
    const rect = tr.getBoundingClientRect();
    const position: "before" | "after" = clientY < rect.top + rect.height / 2 ? "before" : "after";

    const prev = dropTargetRef.current;
    if (prev && prev.id !== targetId) {
      getRowEl(prev.id)?.classList.remove("menuRowDropBefore", "menuRowDropAfter");
    }
    dropTargetRef.current = { id: targetId, position };
    tr.classList.remove("menuRowDropBefore", "menuRowDropAfter");
    tr.classList.add(position === "before" ? "menuRowDropBefore" : "menuRowDropAfter");

    // indent bump preview on the dragged row
    if (dragStartXRef.current !== null) {
      const deltaX = clientX - dragStartXRef.current;
      let indentDelta = 0;
      if (deltaX > 12) indentDelta = 1;
      else if (deltaX < -12) indentDelta = -1;
      const draggedEl = getRowEl(draggingIdRef.current);
      const contentDiv = draggedEl?.querySelector<HTMLElement>("[data-itemcontent]");
      if (contentDiv) {
        const base = contentDiv.dataset.basedepth ?? "0px";
        if (indentDelta === 0) {
          // No horizontal intent yet: keep exact original indent (prevents "jump right" on click)
          contentDiv.style.paddingLeft = base;
          contentDiv.style.transform = "";
        } else {
          const basePx = Number.parseInt(base, 10) || 0;
          const nextPx = Math.max(0, basePx + indentDelta * 18);
          contentDiv.style.paddingLeft = `${nextPx}px`;
          contentDiv.style.transform = `translateX(${indentDelta * 4}px)`;
        }
      }
    }
  }

  function toFriendlyError(e: unknown, fallback = "Couldn't save your changes. Try again."): string {
    if (e instanceof ApiRequestError) {
      if (e.code === "menu_items_invalid") {
        const details = e.data?.details;
        if (Array.isArray(details) && details.length > 0) {
          const msg = (details[0] as { message?: string })?.message;
          if (typeof msg === "string" && msg.trim()) return msg;
        }
        return "Some menu items are incomplete or invalid. Finish required fields and try again.";
      }
      if (e.code === "forbidden") return "You don't have permission to do that.";
      if (e.code === "not_found") return "This menu was not found.";
      if (e.code === "bad_request") return "Some values are invalid. Review your changes and try again.";
      return fallback;
    }
    if (e instanceof Error && e.message.trim()) return e.message;
    return fallback;
  }

  function currentSnapshot(menuNameFallback?: string) {
    return JSON.stringify({
      name: name.trim() || menuNameFallback || menu?.name || "",
      status,
      items: items.map((item) => ({
        id: item.id,
        parent_id: item.parent_id,
        kind: item.kind,
        label: item.label,
        status: item.status,
        page_id: item.page_id,
        ref_menu_id: item.ref_menu_id,
        icon: item.icon,
        sort_order: item.sort_order
      }))
    });
  }

  function requestSave() {
    setSaveTrigger((prev) => prev + 1);
  }

  async function load() {
    if (!menuId) {
      setError("Missing menu id");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [menuData, itemsData, pagesData, meData] = await Promise.all([
        apiFetch<{ menu: Menu }>(`/api/tenant/menus/${encodeURIComponent(menuId)}`),
        apiFetch<{ items: MenuItem[] }>(`/api/tenant/menus/${encodeURIComponent(menuId)}/items`),
        apiFetch<{ pages: PageOption[] }>("/api/tenant/pages?status=published"),
        apiFetch<{ user: { id: string; entitlement?: string } }>("/api/tenant/me").catch(() => null)
      ]);
      setMenu(menuData.menu);
      setName(menuData.menu.name);
      setStatus(menuData.menu.status);
      if (meData) {
        setCallerAccountId(meData.user.id);
        const e = meData.user.entitlement;
        setCallerRole(e === "admin" ? "admin" : e === "builder" ? "builder" : "viewer");
      }
      const normalizedItems = normalizeSort(itemsData.items.map(menuItemFromApiRow));
      setItems(normalizedItems);
      setPages(pagesData.pages);
      try {
        const [menusData, groupsData, usagesData, optData] = await Promise.all([
          apiFetch<{ menus: TenantMenuOption[] }>("/api/tenant/menus"),
          apiFetch<{ groups: Array<{ id: string; name: string; status: "active" | "inactive" }> }>(
            `/api/tenant/menus/${encodeURIComponent(menuId)}/groups`
          ),
          apiFetch<{ usages: Array<{ menu_id: string; menu_name: string; menu_status: string }> }>(
            `/api/tenant/menus/${encodeURIComponent(menuId)}/usages`
          ),
          apiFetch<{ groups: Array<{ id: string; name: string }> }>("/api/tenant/menus/group-options")
        ]);
        setTenantMenus(menusData.menus);
        setMenuGroups(groupsData.groups);
        setMenuUsages(usagesData.usages);
        setGroupOptions(optData.groups);
      } catch {
        setTenantMenus([]);
        setMenuGroups([]);
        setMenuUsages([]);
        setGroupOptions([]);
      }
      baselineSnapshotRef.current = JSON.stringify({
        name: menuData.menu.name,
        status: menuData.menu.status,
        items: normalizedItems.map((item) => ({
          id: item.id,
          parent_id: item.parent_id,
          kind: item.kind,
          label: item.label,
          status: item.status,
          page_id: item.page_id,
          ref_menu_id: item.ref_menu_id,
          icon: item.icon,
          sort_order: item.sort_order
        }))
      });
      hydratedRef.current = true;
      setSaveState("idle");
    } catch (e) {
      setError(toFriendlyError(e, "Couldn't load this menu."));
      hydratedRef.current = false;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [menuId]);

  function updateItem(id: string, patch: Partial<MenuItem>) {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  }

  function withNormalized(mutator: (current: MenuItem[]) => MenuItem[]) {
    setItems((prev) => normalizeSort(mutator(prev)));
  }

  function addAt(parentId: string | null, sortOrder: number) {
    withNormalized((current) => {
      const next = current.map((item) => {
        if (item.parent_id === parentId && item.sort_order >= sortOrder) {
          return { ...item, sort_order: item.sort_order + 1 };
        }
        return item;
      });
      next.push({
        id: crypto.randomUUID(),
        parent_id: parentId,
        kind: "folder",
        label: DEFAULT_NEW_MENU_ITEM_LABEL,
        status: "draft",
        page_id: null,
        ref_menu_id: null,
        icon: null,
        sort_order: sortOrder
      });
      return next;
    });
  }

  function addBelow(rowId: string) {
    const row = items.find((item) => item.id === rowId);
    if (!row) return;
    addAt(row.parent_id, row.sort_order + 1);
  }

  async function removeItem(rowId: string) {
    const item = items.find((it) => it.id === rowId);
    if (!item) return;
    const ok = await confirm.confirm({
      title: "Delete item?",
      detail: `Delete "${item.label}" and its subtree?`,
      confirmLabel: "Delete"
    });
    if (!ok) return;
    withNormalized((current) => {
      const subtreeIds = collectDescendantIds(current, rowId);
      subtreeIds.add(rowId);
      return current.filter((node) => !subtreeIds.has(node.id));
    });
  }

  function moveByDrag(current: MenuItem[], draggedId: string, targetId: string, position: "before" | "after"): MenuItem[] {
    if (draggedId === targetId) return current;
    const subtreeIds = collectDescendantIds(current, draggedId);
    subtreeIds.add(draggedId);
    if (subtreeIds.has(targetId)) return current;

    const dragged = current.find((item) => item.id === draggedId);
    const target = current.find((item) => item.id === targetId);
    if (!dragged || !target) return current;

    const subtree = current.filter((item) => subtreeIds.has(item.id));
    let remaining = normalizeSort(current.filter((item) => !subtreeIds.has(item.id)));

    const newParentId = target.parent_id;
    const siblings = remaining
      .filter((item) => item.parent_id === newParentId)
      .sort((a, b) => a.sort_order - b.sort_order);
    const targetIdx = siblings.findIndex((item) => item.id === target.id);
    if (targetIdx < 0) return current;
    const insertAt = position === "before" ? targetIdx : targetIdx + 1;

    remaining = remaining.map((item) => {
      if (item.parent_id === newParentId && item.sort_order >= insertAt) {
        return { ...item, sort_order: item.sort_order + 1 };
      }
      return item;
    });

    const movedSubtree = subtree.map((item) => {
      if (item.id === draggedId) {
        return { ...item, parent_id: newParentId, sort_order: insertAt };
      }
      return item;
    });

    return normalizeSort([...remaining, ...movedSubtree]);
  }

  function indentByOne(current: MenuItem[], draggedId: string): MenuItem[] {
    const rows = flattenRows(current);
    const rowIndex = rows.findIndex((row) => row.id === draggedId);
    if (rowIndex <= 0) return current;
    const subtreeIds = collectDescendantIds(current, draggedId);
    subtreeIds.add(draggedId);
    for (let i = rowIndex - 1; i >= 0; i--) {
      const candidate = rows[i].item;
      if (candidate.kind !== "folder") continue;
      if (subtreeIds.has(candidate.id)) continue;
      const siblingCount = current.filter((item) => item.parent_id === candidate.id).length;
      return current.map((item) => (item.id === draggedId ? { ...item, parent_id: candidate.id, sort_order: siblingCount } : item));
    }
    return current;
  }

  function outdentByOne(current: MenuItem[], draggedId: string): MenuItem[] {
    const item = current.find((node) => node.id === draggedId);
    if (!item || !item.parent_id) return current;
    const parent = current.find((node) => node.id === item.parent_id);
    if (!parent) return current;
    const newParentId = parent.parent_id;
    const newSortOrder = parent.sort_order + 1;
    const shifted = current.map((node) => {
      if (node.parent_id === newParentId && node.sort_order >= newSortOrder) {
        return { ...node, sort_order: node.sort_order + 1 };
      }
      return node;
    });
    return shifted.map((node) => (node.id === draggedId ? { ...node, parent_id: newParentId, sort_order: newSortOrder } : node));
  }

  async function onMenuStatusChange(nextStatus: MenuStatus) {
    if (!menu) return;
    if (nextStatus === status) return;
    if (nextStatus === "published") {
      const ok = await confirm.confirm({
        title: `Publish '${name || menu.name}'?`,
        detail: "This menu becomes the Pages navigation root. The current published menu becomes Inactive.",
        confirmLabel: "Publish"
      });
      if (!ok) return;
    } else if (status === "published") {
      const ok = await confirm.confirm({
        title: `Remove '${name || menu.name}' as published navigation?`,
        detail: "Pages will fall back to all accessible published pages.",
        confirmLabel: "Remove"
      });
      if (!ok) return;
    }
    setStatus(nextStatus);
    requestSave();
  }

  async function onItemStatusChange(item: MenuItem, nextStatus: MenuItemStatus) {
    if (nextStatus === item.status) return;
    if (nextStatus === "active") {
      if (item.kind === "page" && !item.page_id) {
        toast.push({ kind: "error", message: "Pick a page before activating this report." });
        return;
      }
      let parentId = item.parent_id;
      while (parentId) {
        const parent = items.find((it) => it.id === parentId);
        if (!parent) break;
        if (parent.status !== "active") {
          toast.push({ kind: "error", message: "Active items require active ancestors." });
          return;
        }
        parentId = parent.parent_id;
      }
      updateItem(item.id, { status: nextStatus });
      requestSave();
      return;
    }
    const descendantIds = collectDescendantIds(items, item.id);
    const activeDescendantCount = Array.from(descendantIds).filter((id) => items.find((it) => it.id === id)?.status === "active").length;
    if (item.status === "active" && activeDescendantCount > 0) {
      const ok = await confirm.confirm({
        title: `Demote ${activeDescendantCount} active descendants?`,
        detail: `This will demote ${activeDescendantCount} active descendants to ${nextStatus}.`,
        confirmLabel: "Demote"
      });
      if (!ok) return;
    }
    withNormalized((current) => {
      const descendants = collectDescendantIds(current, item.id);
      return current.map((node) => {
        if (node.id === item.id) return { ...node, status: nextStatus };
        if (descendants.has(node.id) && node.status === "active") return { ...node, status: nextStatus };
        return node;
      });
    });
    requestSave();
  }

  function applyDrop() {
    const activeDraggingId = draggingIdRef.current;
    const activeDropTarget = dropTargetRef.current;
    const deltaX =
      dragCurrentXRef.current !== null && dragStartXRef.current !== null
        ? dragCurrentXRef.current - dragStartXRef.current
        : 0;

    // Clean up DOM and refs before any React state changes
    clearDragDOM();
    draggingIdRef.current = null;
    dropTargetRef.current = null;
    dragStartXRef.current = null;
    dragCurrentXRef.current = null;

    if (!activeDraggingId || !activeDropTarget) return;

    withNormalized((current) => {
      let next = current;
      if (activeDropTarget.id !== activeDraggingId) {
        next = moveByDrag(current, activeDraggingId, activeDropTarget.id, activeDropTarget.position);
      }
      if (deltaX > 12) {
        const target = next.find((item) => item.id === activeDropTarget.id);
        if (target && target.id !== activeDraggingId && target.kind === "folder") {
          const childCount = next.filter((item) => item.parent_id === target.id).length;
          next = next.map((item) => (item.id === activeDraggingId ? { ...item, parent_id: target.id, sort_order: childCount } : item));
        } else {
          next = indentByOne(next, activeDraggingId);
        }
      } else if (deltaX < -12) {
        next = outdentByOne(next, activeDraggingId);
      }
      return next;
    });
    requestSave();
  }

  async function persistCurrent() {
    if (!menu) return;
    if (currentSnapshot() === baselineSnapshotRef.current) {
      setSaveState("idle");
      return;
    }
    const sanitizedItems = items.map(sanitizeMenuItemForPersist);
    const payloadItems = sanitizedItems.map((item) => ({
      id: item.id,
      parent_id: item.parent_id,
      kind: item.kind,
      label: item.label,
      status: item.status,
      page_id: item.page_id,
      ref_menu_id: item.ref_menu_id,
      icon: item.icon,
      sort_order: item.sort_order
    }));
    setSaveState("saving");
    try {
      await apiFetch(`/api/tenant/menus/${encodeURIComponent(menu.id)}`, {
        method: "PATCH",
        body: { name: name.trim() || menu.name, status }
      });
      const putRes = await apiFetch<{ items: MenuItem[] }>(`/api/tenant/menus/${encodeURIComponent(menu.id)}/items`, {
        method: "PUT",
        body: { items: payloadItems }
      });
      const nextItems = normalizeSort(putRes.items.map(menuItemFromApiRow));
      setItems(nextItems);
      baselineSnapshotRef.current = JSON.stringify({
        name: name.trim() || menu.name,
        status,
        items: nextItems.map((item) => ({
          id: item.id,
          parent_id: item.parent_id,
          kind: item.kind,
          label: item.label,
          status: item.status,
          page_id: item.page_id,
          ref_menu_id: item.ref_menu_id,
          icon: item.icon,
          sort_order: item.sort_order
        }))
      });
      if (name.trim() && name.trim() !== menu.name) {
        setMenu({ ...menu, name: name.trim() });
      }
      if (status !== menu.status) {
        setMenu((prev) => (prev ? { ...prev, status } : prev));
      }
      try {
        const [menusData, groupsData, usagesData, optData] = await Promise.all([
          apiFetch<{ menus: TenantMenuOption[] }>("/api/tenant/menus"),
          apiFetch<{ groups: Array<{ id: string; name: string; status: "active" | "inactive" }> }>(
            `/api/tenant/menus/${encodeURIComponent(menu.id)}/groups`
          ),
          apiFetch<{ usages: Array<{ menu_id: string; menu_name: string; menu_status: string }> }>(
            `/api/tenant/menus/${encodeURIComponent(menu.id)}/usages`
          ),
          apiFetch<{ groups: Array<{ id: string; name: string }> }>("/api/tenant/menus/group-options")
        ]);
        setTenantMenus(menusData.menus);
        setMenuGroups(groupsData.groups);
        setMenuUsages(usagesData.usages);
        setGroupOptions(optData.groups);
      } catch {
        /* ignore */
      }
      setSaveState("idle");
    } catch (e) {
      const message = toFriendlyError(e);
      setSaveState("idle");
      toast.push({ kind: "error", message });
    }
  }

  useEffect(() => {
    if (!hydratedRef.current || !menu || saveTrigger === 0) return;
    void persistCurrent();
  }, [saveTrigger, menu]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest(".menuTypePicker") || t.closest(".menuTypeFloatingLayer")) return;
      setTypeMenuItemId(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    function onPointerMove(e: PointerEvent) {
      if (activePointerIdRef.current === null) return;
      if (e.pointerId !== activePointerIdRef.current) return;
      e.preventDefault();
      updateDropFromPointer(e.clientX, e.clientY);
    }
    function onPointerUp(e: PointerEvent) {
      if (activePointerIdRef.current === null) return;
      if (e.pointerId !== activePointerIdRef.current) return;
      e.preventDefault();
      activePointerIdRef.current = null;
      updateDropFromPointer(e.clientX, e.clientY);
      applyDrop();
    }
    function onPointerCancel(e: PointerEvent) {
      if (activePointerIdRef.current === null) return;
      if (e.pointerId !== activePointerIdRef.current) return;
      activePointerIdRef.current = null;
      clearDragDOM();
      draggingIdRef.current = null;
      dropTargetRef.current = null;
      dragStartXRef.current = null;
      dragCurrentXRef.current = null;
    }
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp, { passive: false });
    window.addEventListener("pointercancel", onPointerCancel);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [flatRows]);

  return (
    <PageLayout
      title={
        <span className="breadcrumbTitle">
          <button className="breadcrumbLink" type="button" onClick={() => nav("/app/workspace/menus")}>
            Menus
          </button>
          <span className="breadcrumbSep">/</span>
          <span>{menu?.name ?? "Menu"}</span>
          {saveState === "saving" ? <span className="muted" style={{ marginLeft: 8, fontSize: 14 }}>Saving…</span> : null}
        </span>
      }
    >
      {/* ADR-0068 scope header — shown when we have a menu loaded */}
      {menu && menu.scope && (
        <div style={{ marginBottom: 8 }}>
          <ScopeHeader
            entityKind="menu"
            entityId={menu.id}
            scope={menu.scope}
            ownerGroupId={menu.owner_group_id ?? null}
            ownerGroupName={menu.owner_group_name ?? null}
            ownerAccountId={menu.owner_account_id ?? null}
            callerAccountId={callerAccountId}
            callerRole={callerRole}
            callerGroupIds={callerGroups.map((g) => g.id)}
            onScopeChanged={() => void load()}
            cloneEditorPathPrefix="/app/build/menus"
          />
        </div>
      )}
      <div className="card">
        {loading ? <LoadingState /> : null}
        {!loading && error ? <ErrorState detail={error} /> : null}
        {!loading && !error && menu ? (
          <>
            <div className="toolbar" style={{ marginBottom: 8 }}>
              <div className="field" style={{ minWidth: 260 }}>
                <label className="label">Name</label>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => requestSave()}
                />
              </div>
              <div className="field" style={{ minWidth: 180 }}>
                <label className="label">Status</label>
                <select className="select" value={status} onChange={(e) => void onMenuStatusChange(e.target.value as MenuStatus)}>
                  {MENU_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {menuStatusLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ alignSelf: "end" }}>
                <button
                  className="linkBtn menuLinkAction"
                  type="button"
                  onClick={() => {
                    addAt(null, items.filter((item) => item.parent_id === null).length);
                    requestSave();
                  }}
                >
                  Add item
                </button>
              </div>
            </div>

            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Status</th>
                    <th>Link</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody ref={tbodyRef}>
                  {flatRows.map((row) => {
                    const item = row.item;
                    const hasChildren = items.some((candidate) => candidate.parent_id === item.id);
                    const isBrokenLink = !!item.page_id && !pageById.has(item.page_id);
                    const isBrokenRef = item.kind === "menu_ref" && !!item.ref_menu_id && !menuById.has(item.ref_menu_id);
                    const refMenuChoices = tenantMenus.filter((m) => m.id !== menuId && m.status !== "published");
                    const basePaddingPx = `${row.depth * 18}px`;
                    return (
                      <tr
                        key={item.id}
                        data-rowid={item.id}
                        className=""
                      >
                        <td>
                          <div
                            data-itemcontent
                            data-basedepth={basePaddingPx}
                            style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: basePaddingPx }}
                          >
                            <span
                              className="dragHandle"
                              title="Drag to move"
                              onPointerDown={(e) => startPointerDrag(item.id, e)}
                            >
                              ⠿
                            </span>
                            <MenuItemTypePicker
                              item={item}
                              hasChildren={hasChildren}
                              isOpen={typeMenuItemId === item.id}
                              onToggle={() => setTypeMenuItemId((prev) => (prev === item.id ? null : item.id))}
                              onClose={() => setTypeMenuItemId(null)}
                              updateItem={updateItem}
                              requestSave={requestSave}
                              toastPush={toast.push}
                            />
                            {item.kind === "menu_ref" ? (
                              <span className="muted menuCompactControl" style={{ flex: 1 }}>
                                {item.ref_menu_id
                                  ? (menuById.get(item.ref_menu_id)?.name ?? "…")
                                  : "Select a menu →"}
                              </span>
                            ) : (
                              <input
                                className="input menuCompactControl"
                                value={item.label}
                                onChange={(e) => updateItem(item.id, { label: e.target.value })}
                                onBlur={() => {
                                  if (!item.label.trim()) {
                                    updateItem(item.id, { label: DEFAULT_NEW_MENU_ITEM_LABEL });
                                  }
                                  requestSave();
                                }}
                                aria-label="Item label"
                              />
                            )}
                          </div>
                        </td>
                        <td>
                          <select
                            className="select menuCompactControl"
                            value={item.status}
                            onChange={(e) => void onItemStatusChange(item, e.target.value as MenuItemStatus)}
                          >
                            {ITEM_STATUS_OPTIONS.map((option) => (
                              <option key={option} value={option}>
                                {option[0].toUpperCase() + option.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {item.kind === "menu_ref" ? (
                            <>
                              <select
                                className="select menuCompactControl"
                                value={item.ref_menu_id ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value || null;
                                  updateItem(item.id, { ref_menu_id: value });
                                  requestSave();
                                }}
                              >
                                <option value="">Select menu…</option>
                                {refMenuChoices.map((m) => (
                                  <option key={m.id} value={m.id} disabled={m.status !== "active"} title={m.status !== "active" ? "Set menu to Active to use in navigation" : undefined}>
                                    {m.name}
                                    {m.status !== "active" ? ` (${m.status})` : ""}
                                  </option>
                                ))}
                              </select>
                              {isBrokenRef ? <div className="muted" style={{ color: "var(--warn, #b45309)" }}>Referenced menu not found</div> : null}
                            </>
                          ) : (
                            <>
                              <select
                                className="select menuCompactControl"
                                value={item.page_id ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value || null;
                                  updateItem(item.id, { page_id: value });
                                  requestSave();
                                }}
                              >
                                <option value="">No link</option>
                                {pages.map((page) => (
                                  <option key={page.id} value={page.id}>
                                    {page.title}
                                  </option>
                                ))}
                              </select>
                              {isBrokenLink ? <div className="muted">Linked page is missing or not active.</div> : null}
                              {item.kind === "page" && !item.page_id && !isBrokenLink ? (
                                <div className="muted">Pick a page to activate this report.</div>
                              ) : null}
                            </>
                          )}
                        </td>
                        <td>
                          <div className="actions menuBuilderActions">
                            <button
                              className="linkBtn menuLinkAction"
                              type="button"
                              onClick={() => {
                                addBelow(item.id);
                                requestSave();
                              }}
                            >
                              Add below
                            </button>
                            <button
                              className="linkBtn menuLinkAction linkBtnDanger"
                              type="button"
                              onClick={() => {
                                void removeItem(item.id).then(() => requestSave());
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: 24 }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>Teams ({menuGroups.length})</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "start" }}>
                <div>
                  <div className="label" style={{ marginBottom: 6 }}>
                    Available
                    {groupOptions.filter((g) => !menuGroups.some((m) => m.id === g.id)).length > 0 && (
                      <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>— Ctrl/⌘ multi-select</span>
                    )}
                  </div>
                  <select
                    multiple
                    size={6}
                    className="input"
                    style={{ width: "100%", height: 180 }}
                    value={availableGroupsSelected}
                    onChange={(e) => setAvailableGroupsSelected(Array.from(e.target.selectedOptions, (o) => o.value))}
                    disabled={groupOpsLoading}
                  >
                    {groupOptions
                      .filter((g) => !menuGroups.some((m) => m.id === g.id))
                      .map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 8, justifyContent: "center", paddingTop: 28 }}>
                  <button
                    type="button"
                    className="btn"
                    disabled={availableGroupsSelected.length === 0 || groupOpsLoading}
                    onClick={() => {
                      const toAdd = availableGroupsSelected;
                      if (!toAdd.length) return;
                      setGroupOpsLoading(true);
                      void (async () => {
                        try {
                          await Promise.all(
                            toAdd.map((gid) =>
                              apiFetch(`/api/tenant/menus/${encodeURIComponent(menuId)}/groups`, {
                                method: "POST",
                                body: { group_id: gid }
                              })
                            )
                          );
                          setAvailableGroupsSelected([]);
                          const data = await apiFetch<{ groups: typeof menuGroups }>(
                            `/api/tenant/menus/${encodeURIComponent(menuId)}/groups`
                          );
                          setMenuGroups(data.groups);
                        } catch (err) {
                          toast.push({ kind: "error", message: err instanceof Error ? err.message : String(err) });
                        } finally {
                          setGroupOpsLoading(false);
                        }
                      })();
                    }}
                  >
                    Add →
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={assignedGroupsSelected.length === 0 || groupOpsLoading}
                    onClick={() => {
                      const toRemove = assignedGroupsSelected;
                      if (!toRemove.length) return;
                      setGroupOpsLoading(true);
                      void (async () => {
                        try {
                          await Promise.all(
                            toRemove.map((gid) =>
                              apiFetch(`/api/tenant/menus/${encodeURIComponent(menuId)}/groups/${encodeURIComponent(gid)}`, {
                                method: "DELETE"
                              })
                            )
                          );
                          setAssignedGroupsSelected([]);
                          const data = await apiFetch<{ groups: typeof menuGroups }>(
                            `/api/tenant/menus/${encodeURIComponent(menuId)}/groups`
                          );
                          setMenuGroups(data.groups);
                        } catch (err) {
                          toast.push({ kind: "error", message: err instanceof Error ? err.message : String(err) });
                        } finally {
                          setGroupOpsLoading(false);
                        }
                      })();
                    }}
                  >
                    ← Remove
                  </button>
                </div>

                <div>
                  <div className="label" style={{ marginBottom: 6 }}>
                    Assigned
                    {menuGroups.length > 0 && (
                      <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>— Ctrl/⌘ multi-select</span>
                    )}
                  </div>
                  <select
                    multiple
                    size={6}
                    className="input"
                    style={{ width: "100%", height: 180 }}
                    value={assignedGroupsSelected}
                    onChange={(e) => setAssignedGroupsSelected(Array.from(e.target.selectedOptions, (o) => o.value))}
                    disabled={groupOpsLoading}
                  >
                    {menuGroups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {menuUsages.length > 0 ? (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Referenced by ({menuUsages.length})</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {menuUsages.map((u) => (
                    <li key={u.menu_id}>
                      <Link to={`/app/build/menus/${encodeURIComponent(u.menu_id)}`}>→ {u.menu_name}</Link>
                      <span className="muted" style={{ marginLeft: 8 }}>
                        ({u.menu_status})
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </PageLayout>
  );
}
